/**
 * Settings Page - Admin settings management
 * @file src/web/features/settings/pages/index.tsx
 */

import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardTitle } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Input } from '@voilajsx/uikit/input';
import { Label } from '@voilajsx/uikit/label';
import { Textarea } from '@voilajsx/uikit/textarea';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@voilajsx/uikit/tabs';
import { Settings, Save, Loader2, CheckCircle2, Bell, MessageSquare, Image, Phone, Upload, X, ImageIcon } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { useAuth } from '../../auth';
import { useSettings } from '../hooks/useSettings';
import { useSettingsContext } from '../../../shared/context/SettingsContext';
import type { Settings as SettingsType } from '../types/settings.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const { loading, error, getSettings, updateSettings } = useSettings();
  const { refreshSettings } = useSettingsContext();
  const [, setSettings] = useState<SettingsType | null>(null);
  const [activeSection, setActiveSection] = useState('notifications');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);
  const [heroImageFile, setHeroImageFile] = useState<File | null>(null);
  const [heroImagePreview, setHeroImagePreview] = useState<string>('');
  const [formData, setFormData] = useState({
    notification1: '',
    notification2: '',
    notification3: '',
    update1Title: '',
    update1Description: '',
    update2Title: '',
    update2Description: '',
    update3Title: '',
    update3Description: '',
    update4Title: '',
    update4Description: '',
    update5Title: '',
    update5Description: '',
    advertisementImageUrl: '',
    heroImageUrl: '',
    contactAddress: '',
    contactPhone: '',
    contactEmail: '',
    contactHours: '',
  });
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSettings();
    }
  }, [user]);

  const fetchSettings = async () => {
    const result = await getSettings();
    if (result) {
      setSettings(result);
      setFormData({
        notification1: result.notification1 || '',
        notification2: result.notification2 || '',
        notification3: result.notification3 || '',
        update1Title: result.update1Title || '',
        update1Description: result.update1Description || '',
        update2Title: result.update2Title || '',
        update2Description: result.update2Description || '',
        update3Title: result.update3Title || '',
        update3Description: result.update3Description || '',
        update4Title: result.update4Title || '',
        update4Description: result.update4Description || '',
        update5Title: result.update5Title || '',
        update5Description: result.update5Description || '',
        advertisementImageUrl: result.advertisementImageUrl || '',
        heroImageUrl: result.heroImageUrl || '',
        contactAddress: result.contactAddress || '',
        contactPhone: result.contactPhone || '',
        contactEmail: result.contactEmail || '',
        contactHours: result.contactHours || '',
      });
      setImagePreview(result.advertisementImageUrl || '');
      setHeroImagePreview(result.heroImageUrl || '');
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a PNG or JPG image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = async () => {
    if (!imageFile) {
      alert('Please select an image first');
      return;
    }

    setUploadingImage(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', imageFile);

      const response = await fetch(`${API_URL}/api/settings/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: uploadFormData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();

      // Update form data with the new image URL (signed URL)
      setFormData(prev => ({ ...prev, advertisementImageUrl: data.url }));
      setImagePreview(data.url);
      setImageFile(null);

      // Refresh settings context to update homepage immediately
      await refreshSettings();

      alert('Image uploaded successfully!');
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    setImageFile(null);
    setImagePreview('');
    setFormData({ ...formData, advertisementImageUrl: '' });

    // Save the deletion to database
    const result = await updateSettings({ advertisementImageUrl: '' });
    if (result) {
      await refreshSettings();
      alert('Image deleted successfully!');
    }
  };

  const handleHeroImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a PNG or JPG image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setHeroImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setHeroImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleHeroImageUpload = async () => {
    if (!heroImageFile) {
      alert('Please select an image first');
      return;
    }

    setUploadingHeroImage(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', heroImageFile);

      const response = await fetch(`${API_URL}/api/settings/upload-hero-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: uploadFormData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();

      // Update form data with the new image URL (signed URL)
      setFormData(prev => ({ ...prev, heroImageUrl: data.url }));
      setHeroImagePreview(data.url);
      setHeroImageFile(null);

      // Refresh settings context to update homepage immediately
      await refreshSettings();

      alert('Hero image uploaded successfully!');
    } catch (error) {
      console.error('Hero image upload error:', error);
      alert('Failed to upload hero image. Please try again.');
    } finally {
      setUploadingHeroImage(false);
    }
  };

  const handleRemoveHeroImage = async () => {
    setHeroImageFile(null);
    setHeroImagePreview('');
    setFormData({ ...formData, heroImageUrl: '' });

    // Save the deletion to database
    const result = await updateSettings({ heroImageUrl: '' });
    if (result) {
      await refreshSettings();
      alert('Hero image deleted successfully!');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveSuccess(false);

    const result = await updateSettings(formData);
    if (result) {
      setSettings(result);
      setSaveSuccess(true);

      // Refresh the global settings context to update homepage
      await refreshSettings();

      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  if (!user) {
    return null;
  }

  if (user.role !== 'admin') {
    return (
      <>
        <SEO title="Settings - Access Denied" />
        <div className="container mx-auto px-4 max-w-7xl py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <Card className="max-w-md">
              <CardContent className="p-6">
                <CardTitle>Access Denied</CardTitle>
                <CardDescription>
                  You don't have permission to access this page.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Website Settings - Tuition Point Admin"
        description="Configure website notifications, updates, advertisement, and contact details"
      />
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="space-y-6">
          {/* Page Header */}
          <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 text-gray-900">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Settings className="h-8 w-8 text-purple-600" />
              </div>
              Website Settings
            </h1>
            <p className="text-gray-600 mt-2">
              Configure notifications, latest updates, advertisement, and contact information
            </p>
          </div>

          {/* Settings Form */}
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Left Navigation Menu */}
              <div className="lg:col-span-1">
                <Card className="sticky top-6">
                  <CardContent className="p-4">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 px-4">
                      Settings Navigation
                    </h2>
                    <nav className="space-y-2">
                      <button
                        type="button"
                        onClick={() => setActiveSection('notifications')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                          activeSection === 'notifications'
                            ? 'bg-yellow-100 text-yellow-900 font-semibold'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <Bell className="h-5 w-5" />
                        <span>Notifications</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveSection('updates')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                          activeSection === 'updates'
                            ? 'bg-blue-100 text-blue-900 font-semibold'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <MessageSquare className="h-5 w-5" />
                        <span>Latest Updates</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveSection('advertisement')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                          activeSection === 'advertisement'
                            ? 'bg-emerald-100 text-emerald-900 font-semibold'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <Image className="h-5 w-5" />
                        <span>Advertisement</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveSection('hero')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                          activeSection === 'hero'
                            ? 'bg-cyan-100 text-cyan-900 font-semibold'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <ImageIcon className="h-5 w-5" />
                        <span>Hero Banner</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveSection('contact')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                          activeSection === 'contact'
                            ? 'bg-orange-100 text-orange-900 font-semibold'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        <Phone className="h-5 w-5" />
                        <span>Contact Info</span>
                      </button>
                    </nav>
                  </CardContent>
                </Card>
              </div>

              {/* Right Content Area */}
              <div className="lg:col-span-3 space-y-6">
              {/* Notifications Section */}
              {activeSection === 'notifications' && (
              <Card className="border-l-4 border-l-yellow-500 shadow-md overflow-hidden p-0">
                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 px-6 py-5">
                  <CardTitle className="flex items-center gap-2">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Settings className="h-5 w-5 text-yellow-600" />
                    </div>
                    Scrolling Notifications (Top Bar)
                  </CardTitle>
                  <CardDescription className="mt-1.5">
                    Configure the 3 messages that scroll at the top of the homepage
                  </CardDescription>
                </div>
                <CardContent className="space-y-4 p-6">
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>ℹ️ Note:</strong> At least one notification must be filled to show the yellow announcement bar on the homepage. If all notifications are empty, the bar will be hidden.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notification1">Notification 1</Label>
                    <Input
                      id="notification1"
                      value={formData.notification1}
                      onChange={(e) => setFormData({ ...formData, notification1: e.target.value })}
                      placeholder="New Batch Starting on Monday - Limited Seats Available"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notification2">Notification 2</Label>
                    <Input
                      id="notification2"
                      value={formData.notification2}
                      onChange={(e) => setFormData({ ...formData, notification2: e.target.value })}
                      placeholder="Expert Teachers with 15+ Years Experience - Enroll Today"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notification3">Notification 3</Label>
                    <Input
                      id="notification3"
                      value={formData.notification3}
                      onChange={(e) => setFormData({ ...formData, notification3: e.target.value })}
                      placeholder="93% Average Board Exam Results - Join Our Success Story"
                    />
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Latest Updates Section */}
              {activeSection === 'updates' && (
              <Card className="border-l-4 border-l-blue-500 shadow-md overflow-hidden p-0">
                <div className="bg-gradient-to-r from-blue-50 to-sky-50 px-6 py-5">
                  <CardTitle className="flex items-center gap-2">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Settings className="h-5 w-5 text-blue-600" />
                    </div>
                    Latest Updates (5 Items)
                  </CardTitle>
                  <CardDescription className="mt-1.5">
                    Configure the 5 latest updates displayed on the homepage - changes appear in live preview
                  </CardDescription>
                </div>
                <CardContent className="p-6">
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>ℹ️ Note:</strong> At least one update must be filled to show the Latest Updates block on the homepage. If all updates are empty, the Latest Updates block will be hidden (advertisement will still show if available).
                    </p>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Side - Tabs Editor */}
                    <div>
                      <Tabs defaultValue="update1" className="w-full">
                        <TabsList className="grid w-full grid-cols-5 bg-muted">
                          <TabsTrigger value="update1">1</TabsTrigger>
                          <TabsTrigger value="update2">2</TabsTrigger>
                          <TabsTrigger value="update3">3</TabsTrigger>
                          <TabsTrigger value="update4">4</TabsTrigger>
                          <TabsTrigger value="update5">5</TabsTrigger>
                        </TabsList>
                        {[1, 2, 3, 4, 5].map((num) => (
                          <TabsContent key={num} value={`update${num}`} className="space-y-4 mt-4">
                            <div className="space-y-2">
                              <Label htmlFor={`update${num}Title`}>Title</Label>
                              <Input
                                id={`update${num}Title`}
                                value={formData[`update${num}Title` as keyof typeof formData]}
                                onChange={(e) => setFormData({ ...formData, [`update${num}Title`]: e.target.value })}
                                placeholder="Update title"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor={`update${num}Description`}>Description</Label>
                              <Textarea
                                id={`update${num}Description`}
                                value={formData[`update${num}Description` as keyof typeof formData]}
                                onChange={(e) => setFormData({ ...formData, [`update${num}Description`]: e.target.value })}
                                placeholder="Update description"
                                rows={3}
                              />
                            </div>
                          </TabsContent>
                        ))}
                      </Tabs>
                    </div>

                    {/* Right Side - Live Preview */}
                    <div>
                      <div className="text-sm font-medium text-muted-foreground mb-3">Live Preview</div>
                      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200 max-h-[500px] overflow-y-auto">
                        <h3 className="text-2xl font-bold text-foreground mb-4">Latest Updates</h3>
                        <div className="space-y-4">
                          {[1, 2, 3, 4, 5].map((num) => {
                            const title = formData[`update${num}Title` as keyof typeof formData] as string;
                            const description = formData[`update${num}Description` as keyof typeof formData] as string;

                            if (!title && !description) return null;

                            return (
                              <div key={num} className="border-l-4 border-cyan-500 pl-4 py-2">
                                <h4 className="text-base font-semibold text-foreground mb-1">
                                  {title || `Update ${num} Title`}
                                </h4>
                                <p className="text-sm text-foreground">
                                  {description || 'Update description will appear here...'}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Advertisement Section */}
              {activeSection === 'advertisement' && (
              <Card className="border-l-4 border-l-emerald-500 shadow-md overflow-hidden p-0">
                <div className="bg-gradient-to-r from-emerald-50 to-green-50 px-6 py-5">
                  <CardTitle className="flex items-center gap-2">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Image className="h-5 w-5 text-emerald-600" />
                    </div>
                    Advertisement Image
                  </CardTitle>
                  <CardDescription className="mt-1.5">
                    Upload an advertisement image (PNG or JPG) to display on the homepage
                  </CardDescription>
                </div>
                <CardContent className="p-6 space-y-6">
                  {/* File Upload Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <Label htmlFor="imageFile" className="block mb-2">
                          Upload Image (PNG/JPG, max 5MB)
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id="imageFile"
                            type="file"
                            accept="image/png,image/jpeg,image/jpg"
                            onChange={handleImageSelect}
                            className="cursor-pointer"
                          />
                          {imageFile && (
                            <Button
                              type="button"
                              onClick={handleImageUpload}
                              disabled={uploadingImage}
                              className="min-w-[120px]"
                            >
                              {uploadingImage ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="mr-2 h-4 w-4" />
                                  Upload
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Image Preview */}
                    {imagePreview && (
                      <div className="relative">
                        <Label className="block mb-2">Preview:</Label>
                        <div className="relative inline-block">
                          <img
                            src={imagePreview}
                            alt="Advertisement preview"
                            className="max-w-full max-h-96 rounded-lg border border-gray-200 shadow-md"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={handleRemoveImage}
                            className="absolute top-2 right-2"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        {formData.advertisementImageUrl && (
                          <div className="mt-3">
                            <div className="p-3 bg-gray-50 rounded border border-gray-200">
                              <p className="text-xs text-muted-foreground mb-1 font-medium">Image URL:</p>
                              <p className="text-xs text-gray-700 break-all font-mono">{formData.advertisementImageUrl}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-4 space-y-2">
                    <p className="text-sm text-muted-foreground">
                      💡 <strong>Tip:</strong> Upload a high-quality image for best results. The image will be stored in Wasabi S3 cloud storage.
                    </p>
                    <p className="text-sm text-emerald-600 font-medium">
                      ✨ <strong>Auto-Save:</strong> Uploaded images are automatically saved to the database and URLs are auto-regenerated when they expire!
                    </p>
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Hero Banner Section */}
              {activeSection === 'hero' && (
              <Card className="border-l-4 border-l-cyan-500 shadow-md overflow-hidden p-0">
                <div className="bg-gradient-to-r from-cyan-50 to-sky-50 px-6 py-5">
                  <CardTitle className="flex items-center gap-2">
                    <div className="p-2 bg-cyan-100 rounded-lg">
                      <ImageIcon className="h-5 w-5 text-cyan-600" />
                    </div>
                    Hero Banner Image
                  </CardTitle>
                  <CardDescription className="mt-1.5">
                    Upload the main hero banner image displayed in the homepage hero section
                  </CardDescription>
                </div>
                <CardContent className="p-6 space-y-6">
                  {/* File Upload Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <Label htmlFor="heroImageFile" className="block mb-2">
                          Upload Image (PNG/JPG, max 5MB)
                        </Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id="heroImageFile"
                            type="file"
                            accept="image/png,image/jpeg,image/jpg"
                            onChange={handleHeroImageSelect}
                            className="cursor-pointer"
                          />
                          {heroImageFile && (
                            <Button
                              type="button"
                              onClick={handleHeroImageUpload}
                              disabled={uploadingHeroImage}
                              className="min-w-[120px]"
                            >
                              {uploadingHeroImage ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Upload className="mr-2 h-4 w-4" />
                                  Upload
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Image Preview */}
                    {heroImagePreview && (
                      <div className="relative">
                        <Label className="block mb-2">Preview:</Label>
                        <div className="relative inline-block">
                          <img
                            src={heroImagePreview}
                            alt="Hero banner preview"
                            className="max-w-full max-h-96 rounded-lg border border-gray-200 shadow-md"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={handleRemoveHeroImage}
                            className="absolute top-2 right-2"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        {formData.heroImageUrl && (
                          <div className="mt-3">
                            <div className="p-3 bg-gray-50 rounded border border-gray-200">
                              <p className="text-xs text-muted-foreground mb-1 font-medium">Image URL:</p>
                              <p className="text-xs text-gray-700 break-all font-mono">{formData.heroImageUrl}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-4 space-y-2">
                    <p className="text-sm text-muted-foreground">
                      💡 <strong>Tip:</strong> Use a high-quality image (recommended size: 1200x800px or similar aspect ratio). This image appears in the hero section of the homepage.
                    </p>
                    <p className="text-sm text-cyan-600 font-medium">
                      ✨ <strong>Auto-Save:</strong> Uploaded images are automatically saved to the database and URLs are auto-regenerated when they expire!
                    </p>
                    <p className="text-sm text-gray-500">
                      📌 <strong>Note:</strong> If no hero image is uploaded, the default static image will be used.
                    </p>
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Contact Details Section */}
              {activeSection === 'contact' && (
              <Card className="border-l-4 border-l-orange-500 shadow-md overflow-hidden p-0">
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-6 py-5">
                  <CardTitle className="flex items-center gap-2">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Settings className="h-5 w-5 text-orange-600" />
                    </div>
                    Contact Information (Footer)
                  </CardTitle>
                  <CardDescription className="mt-1.5">
                    Contact details displayed in the website footer
                  </CardDescription>
                </div>
                <CardContent className="space-y-4 p-6">
                  <div className="space-y-2">
                    <Label htmlFor="contactAddress">Address</Label>
                    <Input
                      id="contactAddress"
                      value={formData.contactAddress}
                      onChange={(e) => setFormData({ ...formData, contactAddress: e.target.value })}
                      placeholder="Villa No. 99, Street - 201, Zone - 90, Al Wakra, Qatar"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contactPhone">Phone</Label>
                    <Input
                      id="contactPhone"
                      type="tel"
                      value={formData.contactPhone}
                      onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                      placeholder="+974 3015 8401"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contactEmail">Email</Label>
                    <Input
                      id="contactEmail"
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                      placeholder="tuitionwakra@gmail.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contactHours">Operating Hours</Label>
                    <Textarea
                      id="contactHours"
                      value={formData.contactHours}
                      onChange={(e) => setFormData({ ...formData, contactHours: e.target.value })}
                      placeholder="Sunday - Thursday: 8:00 AM - 9:00 PM | Saturday: 8:00 AM - 5:00 PM | Friday: Closed"
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Save Button for active section */}
              <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                <div>
                  {error && (
                    <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</p>
                  )}
                  {saveSuccess && (
                    <p className="text-sm text-green-600 flex items-center gap-1 bg-green-50 px-3 py-2 rounded">
                      <CheckCircle2 className="h-4 w-4" />
                      Settings saved successfully!
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-semibold"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Settings
                    </>
                  )}
                </Button>
              </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
